import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Toast from 'react-hot-toast';
import { CatalogForm } from '../../../components/CatalogForm/CatalogForm';
import { Validators } from '../../../components/CatalogForm/validators';
import { SucursalService } from '../../../services/SucursalService';

const SucursalForm = () => {
  const routeParams = useParams() || {};
  const id = routeParams.id || null;

  // Options states
  const [coloniasOptions, setColoniasOptions] = useState([]);
  const [cpOptions, setCpOptions] = useState([]);

  // Encargados states (for edit mode)
  const [encargados, setEncargados] = useState([]);
  const [loadingEncargados, setLoadingEncargados] = useState(false);
  const [savingEncargados, setSavingEncargados] = useState(false);

  // Load encargados in edit mode
  const fetchEncargados = useCallback(async (sucursalId) => {
    if (!sucursalId) return;
    setLoadingEncargados(true);
    try {
      const data = await SucursalService.obtenerEncargados(sucursalId);
      setEncargados(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingEncargados(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchEncargados(id);
    }
  }, [id, fetchEncargados]);

  // Handle CP Lookup
  const handleSearchCp = useCallback(async (query) => {
    if (!query || String(query).trim().length < 5) return [];
    const cleanCp = String(query).trim().substring(0, 5);
    const res = await SucursalService.obtenerOpcionesForm({ cp: cleanCp });
    if (res && res.colonias && Array.isArray(res.colonias)) {
      return [{
        value: cleanCp,
        label: `${cleanCp} (${res.colonias.length} colonias encontradas)`,
      }];
    }
    return [{ value: cleanCp, label: cleanCp }];
  }, []);

  const handleCpChange = useCallback(async (val, { setFieldValue }) => {
    if (!val || String(val).trim().length < 5) return;
    const cleanCp = String(val).trim().substring(0, 5);
    const res = await SucursalService.obtenerOpcionesForm({ cp: cleanCp });
    if (res && res.colonias && Array.isArray(res.colonias)) {
      const mapped = res.colonias.map((c) => ({
        value: c.id,
        label: c.colonia,
      }));
      setColoniasOptions(mapped);
      if (mapped.length === 1) {
        setFieldValue('sucursal_id_colonia', mapped[0].value);
      } else {
        setFieldValue('sucursal_id_colonia', '');
      }
    }
  }, []);

  // Pre-load data in edit mode
  const loadData = useCallback(async (recordId) => {
    const data = await SucursalService.obtenerPorId(recordId);
    if (data) {
      // If record has CP, fetch colonias for this CP
      if (data.sucursal_cp) {
        setCpOptions([{ value: String(data.sucursal_cp), label: String(data.sucursal_cp) }]);
        const formOpts = await SucursalService.obtenerOpcionesForm({ cp: data.sucursal_cp });
        if (formOpts && formOpts.colonias && Array.isArray(formOpts.colonias)) {
          setColoniasOptions(
            formOpts.colonias.map((c) => ({ value: c.id, label: c.colonia }))
          );
        }
      }
      // Ensure boolean for sucursal_pt_usar
      data.sucursal_pt_usar = Boolean(data.sucursal_pt_usar);
    }
    return data;
  }, []);

  // Form submit handler
  const handleSubmit = useCallback(async (values) => {
    const payload = {
      ...values,
      id: id,
    };
    return await SucursalService.guardar(payload);
  }, [id]);

  // Encargados management
  const handleAddEncargado = () => {
    setEncargados((prev) => [
      ...prev,
      { id_sucursal: id, se_email: '' },
    ]);
  };

  const handleEmailChange = (index, val) => {
    setEncargados((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], se_email: val };
      return copy;
    });
  };

  const handleDeleteEncargado = async (index, encargado) => {
    if (encargado.id) {
      try {
        const res = await SucursalService.eliminarEncargado(encargado.id);
        if (res && !res.error) {
          Toast.success('Encargado eliminado');
          fetchEncargados(id);
        } else {
          Toast.error(res?.errMsg || 'Error al eliminar encargado');
        }
      } catch (e) {
        Toast.error('Error al procesar eliminación');
      }
    } else {
      setEncargados((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleSaveEncargados = async () => {
    if (!id || savingEncargados) return;

    // Validate emails
    const emailValidator = Validators.email();
    for (let i = 0; i < encargados.length; i++) {
      const email = (encargados[i].se_email || '').trim();
      if (!email) {
        Toast.error(`El correo en la fila #${i + 1} no puede estar vacío`);
        return;
      }
      const err = emailValidator(email);
      if (err) {
        Toast.error(`Fila #${i + 1}: ${err}`);
        return;
      }
    }

    setSavingEncargados(true);
    try {
      const res = await SucursalService.guardarEncargados(id, encargados);
      if (res && !res.error) {
        Toast.success('Encargados guardados correctamente');
        fetchEncargados(id);
      } else {
        Toast.error(res?.errMsg || 'Error al guardar los encargados');
      }
    } catch (e) {
      Toast.error('Error inesperado al guardar encargados');
    } finally {
      setSavingEncargados(false);
    }
  };

  // Form field configurations
  const fields = useMemo(() => [
    {
      name: 'sec_general',
      label: 'Datos de la Sucursal',
      type: 'section',
    },
    {
      name: 'sucursal',
      label: 'Clave',
      type: 'text',
      col: 4,
      required: true,
      placeholder: 'Ej. 01 o GDL',
      validators: [Validators.required('La clave de sucursal es obligatoria')],
    },
    {
      name: 'sucursal_nombre',
      label: 'Nombre de Sucursal',
      type: 'text',
      col: 8,
      required: true,
      placeholder: 'Ej. Guadalajara Matriz',
      validators: [Validators.required('El nombre de sucursal es obligatorio')],
    },
    {
      name: 'sucursal_hora_apertura',
      label: 'Hora de Apertura',
      type: 'time',
      col: 6,
      required: true,
      validators: [Validators.required('La hora de apertura es obligatoria')],
    },
    {
      name: 'sucursal_hora_cierre',
      label: 'Hora de Cierre',
      type: 'time',
      col: 6,
      required: true,
      validators: [Validators.required('La hora de cierre es obligatoria')],
    },
    {
      name: 'sec_domicilio',
      label: 'Domicilio y Ubicación',
      type: 'section',
    },
    {
      name: 'sucursal_cp',
      label: 'Código Postal',
      type: 'autocomplete',
      col: 6,
      required: true,
      placeholder: 'Buscar C.P. (5 dígitos)...',
      minSearchLength: 5,
      debounceMs: 250,
      options: cpOptions,
      onSearch: handleSearchCp,
      onChange: handleCpChange,
      validators: [Validators.required('El código postal es obligatorio')],
    },
    {
      name: 'sucursal_id_colonia',
      label: 'Colonia',
      type: 'select',
      col: 6,
      required: true,
      options: coloniasOptions,
      validators: [Validators.required('La colonia es obligatoria')],
    },
    {
      name: 'sucursal_calle',
      label: 'Calle',
      type: 'text',
      col: 8,
      required: true,
      placeholder: 'Ej. Av. Vallarta',
      validators: [Validators.required('La calle es obligatoria')],
    },
    {
      name: 'sucursal_numero',
      label: 'Número Exterior',
      type: 'text',
      col: 4,
      required: true,
      placeholder: 'Ej. 1234',
      validators: [Validators.required('El número exterior es obligatorio')],
    },
    {
      name: 'sucursal_lat',
      label: 'Latitud',
      type: 'text',
      col: 6,
      placeholder: 'Ej. 20.659698',
    },
    {
      name: 'sucursal_lon',
      label: 'Longitud',
      type: 'text',
      col: 6,
      placeholder: 'Ej. -103.349609',
    },
    {
      name: 'sec_pagos',
      label: 'Pagos a Transportista',
      type: 'section',
    },
    {
      name: 'sucursal_pt_usar',
      label: 'Utilizar esta sucursal para calcular pagos a transportista',
      type: 'checkbox',
      col: 12,
    },
    {
      name: 'sucursal_pt_locales',
      label: 'Entregas Locales',
      type: 'number',
      col: 4,
      placeholder: 'Porcentaje o importe',
      visible: (values) => Boolean(values.sucursal_pt_usar),
    },
    {
      name: 'sucursal_pt_autoservicios',
      label: 'Autoservicios',
      type: 'number',
      col: 4,
      placeholder: 'Porcentaje o importe',
      visible: (values) => Boolean(values.sucursal_pt_usar),
    },
    {
      name: 'sucursal_pt_foraneas',
      label: 'Entregas Foráneas',
      type: 'number',
      col: 4,
      placeholder: 'Porcentaje o importe',
      visible: (values) => Boolean(values.sucursal_pt_usar),
    },
    {
      name: 'sucursal_pt_con_entrega',
      label: 'Con Entrega',
      type: 'number',
      col: 6,
      placeholder: 'Porcentaje o importe',
      visible: (values) => Boolean(values.sucursal_pt_usar),
    },
    {
      name: 'sucursal_pt_sin_entrega',
      label: 'Sin Entrega',
      type: 'number',
      col: 6,
      placeholder: 'Porcentaje o importe',
      visible: (values) => Boolean(values.sucursal_pt_usar),
    },
  ], [
    coloniasOptions,
    cpOptions,
    handleSearchCp,
    handleCpChange,
  ]);

  return (
    <div className="space-y-6">
      <CatalogForm
        title="Sucursal"
        subtitle="Administración de sucursales operativas, horarios, domicilio y comisiones"
        fields={fields}
        initialValues={{
          sucursal: '',
          sucursal_nombre: '',
          sucursal_hora_apertura: '09:00',
          sucursal_hora_cierre: '18:00',
          sucursal_cp: '',
          sucursal_id_colonia: '',
          sucursal_calle: '',
          sucursal_numero: '',
          sucursal_lat: '',
          sucursal_lon: '',
          sucursal_pt_usar: false,
          sucursal_pt_locales: '',
          sucursal_pt_autoservicios: '',
          sucursal_pt_foraneas: '',
          sucursal_pt_con_entrega: '',
          sucursal_pt_sin_entrega: '',
        }}
        id={id}
        loadData={loadData}
        onSubmit={handleSubmit}
        backUrl="/Catalogo/Sucursales"
      />

      {/* Encargados Section (Visible in Edit Mode) */}
      {id && (
        <div className="w-full max-w-5xl mx-auto bg-white rounded-xl shadow-xs border border-gray-100 p-6 md:p-8">
          <div className="flex items-center justify-between pb-4 border-b border-gray-100 mb-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Encargados de Sucursal</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Direcciones de correo electrónico asignadas a la sucursal para notificaciones
              </p>
            </div>
            <button
              type="button"
              onClick={handleAddEncargado}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors cursor-pointer min-h-[36px]"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Agregar Encargado</span>
            </button>
          </div>

          {loadingEncargados ? (
            <div className="p-8 text-center text-sm text-gray-400">Cargando encargados...</div>
          ) : encargados.length === 0 ? (
            <div className="p-8 text-center bg-gray-50 rounded-lg border border-dashed border-gray-200 text-sm text-gray-500">
              No hay encargados registrados para esta sucursal. Haga clic en <strong>Agregar Encargado</strong> para añadir uno.
            </div>
          ) : (
            <div className="space-y-3">
              {encargados.map((encargado, idx) => (
                <div key={encargado.id || idx} className="flex items-center gap-3">
                  <div className="flex-1">
                    <input
                      type="email"
                      value={encargado.se_email || ''}
                      onChange={(e) => handleEmailChange(idx, e.target.value)}
                      placeholder="correo@ejemplo.com"
                      className="w-full px-3.5 py-2 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 min-h-[44px]"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteEncargado(idx, encargado)}
                    aria-label={`Eliminar encargado ${idx + 1}`}
                    className="p-2.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}

              <div className="pt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveEncargados}
                  disabled={savingEncargados}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-xs transition-colors cursor-pointer disabled:opacity-50 min-h-[44px]"
                >
                  {savingEncargados && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  <span>Guardar Encargados</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SucursalForm;
